import { useCallback, useEffect, useRef, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import {
  downloadMissionSignatureFile,
  fetchMissionSignatureStatus,
  relaunchMissionSignature,
  resetMissionSignature,
  startMissionSignature,
  syncMissionSignature,
  type MissionSignatureStatus,
  type SignatureStep,
} from '../utils/missionSignatureApi';

type UseMissionSignatureArgs = {
  hubBaseUrl: string;
  nonce: string;
  missionId: number;
  enabled: boolean;
  // Fired exactly once when the signature transitions to `done` (mission promoted to signé),
  // so the page can refresh its summary and flip the progress bar / hide the Signer button.
  onCompleted?: () => void;
};

// Outcome of the last "Relancer le signataire" or "Annuler la signature". Kept apart from `error`,
// which every poll tick and status read clears: a 429 "too soon" or a refused cancel must stay on
// screen until the user acts again.
export type ActionFeedback = { kind: 'success' | 'error'; message: string } | null;

export type SignatureDocumentKind = 'signed' | 'attestation';

// Used only when the server sends no Content-Disposition file name.
const FALLBACK_FILENAMES: Record<SignatureDocumentKind, string> = {
  signed: 'document-signe.pdf',
  attestation: 'attestation-signature.pdf',
};

const saveBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

type UseMissionSignatureResult = {
  status: MissionSignatureStatus | null;
  loading: boolean;
  busy: boolean;
  error: string;
  polling: boolean;
  pollingTimedOut: boolean;
  syncing: boolean;
  relaunching: boolean;
  relaunchFeedback: ActionFeedback;
  cancelling: boolean;
  cancelFeedback: ActionFeedback;
  downloading: SignatureDocumentKind | null;
  downloadError: string;
  refresh: () => Promise<void>;
  syncNow: () => Promise<void>;
  relaunch: () => Promise<void>;
  cancel: () => Promise<void>;
  downloadDocument: (kind: SignatureDocumentKind) => Promise<void>;
  launchSignature: (includeCompanySignatory?: boolean) => Promise<string | null>;
  resetAndLaunch: () => Promise<string | null>;
};

// Poll cadence for a signature in flight. The mission page mounts a status fetch, but after the
// user signs (in a separate tab) nothing else advances the UI — so we poll until the mission is
// promoted (`done`) instead of forcing the user to reload.
const POLL_INTERVAL_MS = 5000;
// JeSignExpert has reported `finished` (step import_pending/complete) but our side has not promoted
// yet: the webhook is normally imminent, so we wait this long before actively driving a POST /sync
// (which finalizes + promotes) rather than hammering JeSignExpert on every tick.
const SYNC_ESCALATE_AFTER_TICKS = 3;
// Still `in_progress`: JeSignExpert's state is UNKNOWN to us. The webhook is the only thing that
// would reveal a completed ceremony, so it must NEVER be a precondition for asking — that is the
// defect this constant exists to close (20 August 2026: two signatures completed, no webhook ever
// arrived, and the page polled an unchanging state until the hourly cron). Slower cadence than the
// finished case, and repeating, because signing takes minutes and can land on any tick.
export const IN_PROGRESS_SYNC_TICKS = 6;
// Hard ceiling so an abandoned tab does not poll forever. "Vérifier maintenant" does not depend on
// it: the panel offers it for the whole in-progress collecte; the cap only adds the "takes longer
// than expected" notice (and the button for a finished-but-not-imported collecte).
const MAX_POLL_TICKS = 30;

// Steps where signing is under way and the UI still needs to converge on `done`.
const isInFlightStep = (step: SignatureStep | undefined): boolean =>
  step === 'in_progress' || step === 'import_pending' || step === 'complete';

// Steps where JeSignExpert is finished and only local import/promotion remains, so a POST /sync
// can actively complete the job when the webhook is late or missed.
const isJseFinishedStep = (step: SignatureStep | undefined): boolean =>
  step === 'import_pending' || step === 'complete';

export const useMissionSignature = ({
  hubBaseUrl,
  nonce,
  missionId,
  enabled,
  onCompleted,
}: UseMissionSignatureArgs): UseMissionSignatureResult => {
  const [status, setStatus] = useState<MissionSignatureStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A failed launch or reset-and-relaunch keeps its own message: every status read (the refresh
  // that follows the failure, then each poll tick or sync) starts by clearing `error`, which would
  // erase the server's refusal before it is read. Cleared only by the next launch attempt or cancel.
  const [launchError, setLaunchError] = useState('');
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [relaunching, setRelaunching] = useState(false);
  const [relaunchFeedback, setRelaunchFeedback] = useState<ActionFeedback>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelFeedback, setCancelFeedback] = useState<ActionFeedback>(null);
  const [downloading, setDownloading] = useState<SignatureDocumentKind | null>(null);
  const [downloadError, setDownloadError] = useState('');
  // Refs, not state, guard re-entry: a double click lands before React re-renders the button
  // disabled, and the poll may fire a sync while a manual one is still in flight.
  const syncInFlightRef = useRef(false);
  const relaunchInFlightRef = useRef(false);
  const cancelInFlightRef = useRef(false);

  // preserveStatusOnError keeps the last known status on failure. A poll tick must NOT null it: a
  // single transient blip would otherwise drop the step, tear down the loop, and collapse the panel
  // to a bare error banner — forcing the full reload the polling exists to avoid. The initial load
  // still nulls so a hard first-fetch failure surfaces the error state.
  const fetchStatusInto = useCallback(async (preserveStatusOnError: boolean): Promise<void> => {
    if (!enabled) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const next = await fetchMissionSignatureStatus({ hubBaseUrl, nonce }, missionId);
      setStatus(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : __('Impossible de charger la signature.'));
      if (!preserveStatusOnError) {
        setStatus(null);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, hubBaseUrl, missionId, nonce]);

  const refresh = useCallback((): Promise<void> => fetchStatusInto(false), [fetchStatusInto]);

  // Active recovery: pull the live JeSignExpert state and finalize + promote when finished. Used by
  // the poll escalation and the manual "Vérifier maintenant" button. One sync at a time: a call
  // made while another is in flight is dropped (it would ask JeSignExpert the same question).
  const syncNow = useCallback(async (): Promise<void> => {
    if (!enabled || syncInFlightRef.current) {
      return;
    }

    syncInFlightRef.current = true;
    setSyncing(true);
    setLoading(true);
    setError('');
    setPollingTimedOut(false);

    try {
      const next = await syncMissionSignature({ hubBaseUrl, nonce }, missionId);
      setStatus(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : __('Impossible de synchroniser la signature.'));
    } finally {
      syncInFlightRef.current = false;
      setSyncing(false);
      setLoading(false);
    }
  }, [enabled, hubBaseUrl, missionId, nonce]);

  // "Relancer le signataire": JeSignExpert re-sends the invitation of the collecte in flight. On
  // success the status is read once more (the signing URL or state may have moved); on failure the
  // server's message is shown as-is (409 expired / not connected, 429 too soon, 502 refused).
  const relaunch = useCallback(async (): Promise<void> => {
    if (!enabled || relaunchInFlightRef.current) {
      return;
    }

    relaunchInFlightRef.current = true;
    setRelaunching(true);
    setRelaunchFeedback(null);
    setCancelFeedback(null);

    let relaunched = false;
    try {
      await relaunchMissionSignature({ hubBaseUrl, nonce }, missionId);
      relaunched = true;
      setRelaunchFeedback({ kind: 'success', message: __('Signataire relancé.') });
    } catch (e) {
      setRelaunchFeedback({
        kind: 'error',
        message: e instanceof Error ? e.message : __('Impossible de relancer le signataire.'),
      });
    } finally {
      relaunchInFlightRef.current = false;
      setRelaunching(false);
    }

    if (relaunched) {
      await fetchStatusInto(true);
    }
  }, [enabled, fetchStatusInto, hubBaseUrl, missionId, nonce]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Fire onCompleted once, only on a genuine transition into `done` — never when a mission that is
  // already signé is loaded fresh (prevStep null → no celebration on first paint).
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const prevStepRef = useRef<SignatureStep | null>(null);
  useEffect(() => {
    const step = status?.step ?? null;
    if (step === 'done' && prevStepRef.current !== null && prevStepRef.current !== 'done') {
      onCompletedRef.current?.();
    }
    prevStepRef.current = step;
  }, [status?.step]);

  // Polling loop. Structural inputs (enabled, in-flight, timed-out) drive setup/teardown; the fetch
  // callbacks live in refs so ticking never re-subscribes the interval (react-base §5). Poll ticks
  // preserve the last status on error so a blip cannot end the loop.
  const pollFetchRef = useRef(fetchStatusInto);
  pollFetchRef.current = fetchStatusInto;
  const syncNowRef = useRef(syncNow);
  syncNowRef.current = syncNow;
  const currentStep = status?.step;
  const stepRef = useRef(currentStep);
  stepRef.current = currentStep;
  const shouldPoll = enabled && !pollingTimedOut && isInFlightStep(currentStep);

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    let ticks = 0;
    let lastSyncTick = 0;

    const interval = window.setInterval(() => {
      // Pause (do not consume the budget) while the tab is hidden.
      if (document.visibilityState === 'hidden') {
        return;
      }

      ticks += 1;
      if (ticks > MAX_POLL_TICKS) {
        setPollingTimedOut(true);
        return;
      }

      // Two cadences. Once JeSignExpert is known finished only local promotion remains, so a single
      // nudge settles it. While still in flight we know nothing, and a GET can only ever echo our
      // own unchanged row — so the sync must keep probing on its own schedule.
      const jseFinished = isJseFinishedStep(stepRef.current);
      const dueSync = jseFinished
        ? lastSyncTick === 0 && ticks >= SYNC_ESCALATE_AFTER_TICKS
        : ticks - lastSyncTick >= IN_PROGRESS_SYNC_TICKS;

      if (dueSync) {
        lastSyncTick = ticks;
        void syncNowRef.current();
        return;
      }

      void pollFetchRef.current(true);
    }, POLL_INTERVAL_MS);

    return () => { window.clearInterval(interval); };
  }, [shouldPoll]);

  // Coming back to the tab is the strongest signal available that the state moved: the user was
  // away in the JeSignExpert ceremony, which opens in its own tab and redirects back here. Ask at
  // once instead of waiting out the cadence — this is the moment the answer changed.
  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void syncNowRef.current();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => { document.removeEventListener('visibilitychange', onVisibilityChange); };
  }, [shouldPoll]);

  // Downloads the signed document or the attestation the status points to (partial close).
  const downloadDocument = useCallback(async (kind: SignatureDocumentKind): Promise<void> => {
    const url = kind === 'signed' ? status?.signed_download_url : status?.attestation_download_url;
    if (url === null || url === undefined || url === '' || downloading !== null) {
      return;
    }

    setDownloading(kind);
    setDownloadError('');

    try {
      const file = await downloadMissionSignatureFile({ hubBaseUrl, nonce }, url);
      saveBlob(file.blob, file.filename ?? FALLBACK_FILENAMES[kind]);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : __('Échec du téléchargement.'));
    } finally {
      setDownloading(null);
    }
  }, [downloading, hubBaseUrl, nonce, status?.attestation_download_url, status?.signed_download_url]);

  const launchSignature = useCallback(
    async (includeCompanySignatory = false): Promise<string | null> => {
    if (busy) {
      return null;
    }

    setBusy(true);
    setLaunchError('');
    setCancelFeedback(null);

    try {
      if (status?.can_resume_signing === true && status.signing_url !== null && status.signing_url !== '') {
        return status.signing_url;
      }

      // When can_start is false, the panel already renders the correct step and CTA.
      // Return null so the caller scrolls to the panel instead of showing a generic error.
      if (status?.can_start !== true) {
        return null;
      }

      const started = await startMissionSignature(
        { hubBaseUrl, nonce },
        missionId,
        includeCompanySignatory,
      );
      await refresh();
      return started.signing_url !== '' ? started.signing_url : null;
    } catch (e) {
      setLaunchError(e instanceof Error ? e.message : __('Impossible de lancer la signature.'));
      return null;
    } finally {
      setBusy(false);
    }
    },
    [busy, hubBaseUrl, missionId, nonce, refresh, status],
  );

  // "Annuler la signature" (§4.7.3): cancel the collecte in flight (JSE + local) and start nothing.
  // The status is read again either way: on success it is back to `ready` and Signer launches a new
  // collecte; a 409 "already finished" means the server imported the signature instead. Holds
  // `busy` too, so Signer cannot launch while the cancel is in flight.
  const cancel = useCallback(async (): Promise<void> => {
    if (!enabled || busy || cancelInFlightRef.current) {
      return;
    }

    cancelInFlightRef.current = true;
    setCancelling(true);
    setBusy(true);
    setCancelFeedback(null);
    setRelaunchFeedback(null);
    setLaunchError('');

    try {
      await resetMissionSignature({ hubBaseUrl, nonce }, missionId);
      setCancelFeedback({ kind: 'success', message: __('Collecte annulée.') });
    } catch (e) {
      setCancelFeedback({
        kind: 'error',
        message: e instanceof Error ? e.message : __('Impossible d’annuler la signature.'),
      });
    } finally {
      cancelInFlightRef.current = false;
      setCancelling(false);
      setBusy(false);
    }

    await fetchStatusInto(true);
  }, [busy, enabled, fetchStatusInto, hubBaseUrl, missionId, nonce]);

  // Recovery for a stuck collecte: cancel it (JSE + local), then start a fresh transaction so the
  // signatory gets a new session instead of resuming the wedged one. Runs from the Signer button
  // when the current stage is a stuck in-progress collecte; "Annuler la signature" ({@link cancel})
  // is the explicit control that cancels without relaunching.
  const resetAndLaunch = useCallback(async (): Promise<string | null> => {
    if (busy) {
      return null;
    }

    setBusy(true);
    setLaunchError('');
    setCancelFeedback(null);

    try {
      await resetMissionSignature({ hubBaseUrl, nonce }, missionId);
      const started = await startMissionSignature({ hubBaseUrl, nonce }, missionId);
      await refresh();
      return started.signing_url !== '' ? started.signing_url : null;
    } catch (e) {
      setLaunchError(e instanceof Error ? e.message : __('Impossible de relancer la signature.'));
      await refresh();
      return null;
    } finally {
      setBusy(false);
    }
  }, [busy, hubBaseUrl, missionId, nonce, refresh]);

  return {
    status,
    loading,
    busy,
    error: launchError !== '' ? launchError : error,
    polling: shouldPoll,
    pollingTimedOut,
    syncing,
    relaunching,
    relaunchFeedback,
    cancelling,
    cancelFeedback,
    downloading,
    downloadError,
    refresh,
    syncNow,
    relaunch,
    cancel,
    downloadDocument,
    launchSignature,
    resetAndLaunch,
  };
};
